import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import MainLayout from '../../layouts/MainLayout';
import AuthLayout from '../../layouts/AuthLayout';
import RoleGuard from './RoleGuard';
import HomeRedirect from './HomeRedirect';
import { routeAccess } from './routeAccess';
import { buildAppPath, type AppRouteKey } from '../../utils/navigation';

const LoginPage = lazy(() => import('../../pages/LoginPage'));
const DashboardPage = lazy(() => import('../../pages/DashboardPage'));
const CompaniesPage = lazy(() => import('../../pages/CompaniesPage'));
const UsersPage = lazy(() => import('../../pages/UsersPage'));
const ReportsPage = lazy(() => import('../../pages/ReportsPage'));
const UnauthorizedPage = lazy(() => import('../../pages/UnauthorizedPage'));
const TicketsPage = lazy(() => import('../../pages/TicketsPage'));
const ConfigurationPage = lazy(() => import('../../pages/ConfigurationPage'));
const WeighingPage = lazy(() => import('../../pages/WeighingPage'));

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authState } = useAuth();
  if (authState.isLoading) {
    return null;
  }
  return authState.isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const TenantRouteRedirect: React.FC<{ routeKey: AppRouteKey }> = ({ routeKey }) => {
  const { authState } = useAuth();
  return <Navigate to={buildAppPath(routeKey, authState)} replace />;
};

const Router: React.FC = () => {
  return (
    <BrowserRouter>
      <Suspense
        fallback={(
          <div className="min-h-screen bg-slate-100 flex items-center justify-center">
            <div className="text-sm text-slate-600" role="status" aria-live="polite">
              Chargement…
            </div>
          </div>
        )}
      >
        <Routes>
          <Route path="/login" element={
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          } />
          
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          
          <Route path="/" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }>
            <Route index element={<HomeRedirect />} />
            
            {/* Dashboard route - Manager, Accounting - Super Admin and Agent excluded */}
            <Route path="dashboard" element={<TenantRouteRedirect routeKey="dashboard" />} />
            <Route path=":companySlug/dashboard" element={
              <RoleGuard roles={routeAccess.dashboard.roles} permissions={routeAccess.dashboard.permissions} excludedRoles={routeAccess.dashboard.excludedRoles ?? []}>
                <DashboardPage />
              </RoleGuard>
            } />
            
            {/* Weighing route - Manager and Agent - Super Admin excluded */}
            <Route path="weighing" element={<TenantRouteRedirect routeKey="weighing" />} />
            <Route path=":companySlug/weighing" element={
              <RoleGuard roles={routeAccess.weighing.roles} permissions={routeAccess.weighing.permissions} excludedRoles={routeAccess.weighing.excludedRoles ?? []}>
                <WeighingPage />
              </RoleGuard>
            } />
            
            {/* Tickets route - Manager, Accounting and Agent - Super Admin excluded */}
            <Route path="tickets" element={<TenantRouteRedirect routeKey="tickets" />} />
            <Route path=":companySlug/tickets" element={
              <RoleGuard roles={routeAccess.tickets.roles} permissions={routeAccess.tickets.permissions} excludedRoles={routeAccess.tickets.excludedRoles ?? []}>
                <TicketsPage />
              </RoleGuard>
            } />
            
            {/* Reports route - Manager and Accounting - Super Admin excluded */}
            <Route path="reports" element={<TenantRouteRedirect routeKey="reports" />} />
            <Route path=":companySlug/reports" element={
              <RoleGuard roles={routeAccess.reports.roles} permissions={routeAccess.reports.permissions} excludedRoles={routeAccess.reports.excludedRoles ?? []}>
                <ReportsPage />
              </RoleGuard>
            } />
            
            {/* Companies route - Super Admin only */}
            <Route path="companies" element={
              <RoleGuard roles={routeAccess.companies.roles} permissions={routeAccess.companies.permissions} excludedRoles={routeAccess.companies.excludedRoles ?? []}>
                <CompaniesPage />
              </RoleGuard>
            } />
            
            {/* Users route - Super Admin only */}
            <Route path="users" element={
              <RoleGuard roles={routeAccess.users.roles} permissions={routeAccess.users.permissions} excludedRoles={routeAccess.users.excludedRoles ?? []}>
                <UsersPage />
              </RoleGuard>
            } />
            
            {/* Configuration route - Manager only - Super Admin excluded */}
            <Route path="configuration" element={<TenantRouteRedirect routeKey="configuration" />} />
            <Route path=":companySlug/configuration" element={
              <RoleGuard roles={routeAccess.configuration.roles} permissions={routeAccess.configuration.permissions} excludedRoles={routeAccess.configuration.excludedRoles ?? []}>
                <ConfigurationPage />
              </RoleGuard>
            } />
          </Route>
          
          {/* Rediriger l'ancienne route vers la nouvelle */}
          <Route path="/pesage" element={<Navigate to="/weighing" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default Router;
